import { PaginationState, Updater } from "@tanstack/react-table";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

type FilterRecord<TKey extends string> = Record<TKey, string>;

type UseServerTableUrlStateOptions<TKey extends string> = {
  defaultPageSize?: number;
  filterDefaults: FilterRecord<TKey>;
};

type UseServerTableUrlStateResult<TKey extends string> = {
  pagination: PaginationState;
  globalFilter: string;
  filters: FilterRecord<TKey>;
  setPagination: (updater: Updater<PaginationState>) => void;
  setGlobalFilter: (value: string) => void;
  setFilter: (name: TKey, value?: string) => void;
};

export default function useServerTableUrlState<TKey extends string>({
  defaultPageSize = 40,
  filterDefaults,
}: UseServerTableUrlStateOptions<TKey>): UseServerTableUrlStateResult<TKey> {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = normalizeInteger(searchParams.get("page"), 1, 1);
  const pageSize = normalizeInteger(
    searchParams.get("pageSize"),
    defaultPageSize,
    1,
    100,
  );
  const globalFilter = searchParams.get("q") ?? "";

  const filters = useMemo(
    () =>
      Object.entries(filterDefaults).reduce((acc, [key, defaultValue]) => {
        acc[key as TKey] = searchParams.get(key) ?? defaultValue;
        return acc;
      }, {} as FilterRecord<TKey>),
    [filterDefaults, searchParams],
  );

  const applySearchParams = (updater: (params: URLSearchParams) => void) => {
    setSearchParams((prev) => {
      const previousSerializedParams = prev.toString();
      const params = new URLSearchParams(prev);
      updater(params);

      if (params.toString() === previousSerializedParams) {
        return prev;
      }

      return params;
    }, { replace: true });
  };

  const setPagination = (updater: Updater<PaginationState>) => {
    const current: PaginationState = {
      pageIndex: page - 1,
      pageSize,
    };
    const next =
      typeof updater === "function"
        ? updater(current)
        : updater;

    if (
      next.pageIndex === current.pageIndex &&
      next.pageSize === current.pageSize
    ) {
      return;
    }

    applySearchParams((params) => {
      params.set("page", String(next.pageIndex + 1));
      params.set("pageSize", String(next.pageSize));
    });
  };

  const setGlobalFilter = (value: string) => {
    const normalizedValue = value.trim();
    if (normalizedValue === globalFilter.trim()) {
      return;
    }

    applySearchParams((params) => {
      if (normalizedValue.length > 0) {
        params.set("q", normalizedValue);
      } else {
        params.delete("q");
      }
      params.set("page", "1");
    });
  };

  const setFilter = (name: TKey, value?: string) => {
    const defaultValue = filterDefaults[name];
    const normalizedValue = !value || value === defaultValue ? defaultValue : value;
    if (normalizedValue === filters[name]) {
      return;
    }

    applySearchParams((params) => {
      if (!value || value === defaultValue) {
        params.delete(name);
      } else {
        params.set(name, value);
      }
      params.set("page", "1");
    });
  };

  return {
    pagination: {
      pageIndex: page - 1,
      pageSize,
    },
    globalFilter,
    filters,
    setPagination,
    setGlobalFilter,
    setFilter,
  };
}

const normalizeInteger = (
  value: string | null,
  fallback: number,
  min: number,
  max?: number,
) => {
  if (!value) {
    return fallback;
  }

  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return fallback;
  }

  if (parsed < min) {
    return min;
  }

  if (max !== undefined && parsed > max) {
    return max;
  }

  return parsed;
};
